// hooks/useModalHistory.js
import { useEffect, useRef, useCallback } from 'react';

// Global state to track modals
let globalModalStack = [];
let globalHistoryListener = null;
let isProcessingHistory = false;

const useModalHistory = (isOpen, onClose, modalId = null) => {
  const modalIdRef = useRef(modalId || `modal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const hasRegisteredHistory = useRef(false);
  const isClosingFromBackButton = useRef(false);

  // Setup global history listener (only once)
  useEffect(() => {
    if (!globalHistoryListener) {
      globalHistoryListener = (event) => {
        if (isProcessingHistory) return;
        
        const state = event.state || {};
        const targetModalCount = state.modalCount || 0;
        
        if (globalModalStack.length > targetModalCount) {
          isProcessingHistory = true;
          
          // Close modals from the top of the stack
          const modalsToClose = globalModalStack.length - targetModalCount;
          
          for (let i = 0; i < modalsToClose; i++) {
            const modalToClose = globalModalStack.pop();
            if (modalToClose && modalToClose.onClose) {
              modalToClose.isClosingFromBack = true;
              modalToClose.onClose();
            }
          }
          
          setTimeout(() => {
            isProcessingHistory = false;
          }, 50);
        }
      };
      
      window.addEventListener('popstate', globalHistoryListener);
    }

    // Cleanup global listener when no modals are open
    return () => {
      if (globalModalStack.length === 0 && globalHistoryListener) {
        window.removeEventListener('popstate', globalHistoryListener);
        globalHistoryListener = null;
      }
    };
  }, []);

  // Handle modal opening
  useEffect(() => {
    if (isOpen && !hasRegisteredHistory.current) {
      hasRegisteredHistory.current = true;
      isClosingFromBackButton.current = false;
      
      // Add to global stack
      const modalEntry = {
        id: modalIdRef.current,
        onClose: () => {
          isClosingFromBackButton.current = true;
          onClose();
        },
        isClosingFromBack: false
      };
      
      globalModalStack.push(modalEntry);
      
      // Push history state
      const newState = {
        modalCount: globalModalStack.length,
        timestamp: Date.now()
      };
      
      window.history.pushState(newState, '', window.location.href);
    }
  }, [isOpen, onClose]);

  // Handle modal closing
  useEffect(() => {
    if (!isOpen && hasRegisteredHistory.current) {
      hasRegisteredHistory.current = false;
      
      // Remove from global stack
      const modalIndex = globalModalStack.findIndex(m => m.id === modalIdRef.current);
      if (modalIndex !== -1) {
        globalModalStack.splice(modalIndex, 1);
      }
      
      // ✅ FIX: Replace history state instead of navigating back
      if (!isClosingFromBackButton.current && !isProcessingHistory) {
        setTimeout(() => {
          const currentState = window.history.state || {};
          if (currentState.modalCount > globalModalStack.length) {
            // Replace current state with updated modal count instead of going back
            const updatedState = {
              ...currentState,
              modalCount: globalModalStack.length,
              timestamp: Date.now()
            };
            window.history.replaceState(updatedState, '', window.location.href);
          }
        }, 10);
      }
      
      isClosingFromBackButton.current = false;
    }
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (hasRegisteredHistory.current) {
        const modalIndex = globalModalStack.findIndex(m => m.id === modalIdRef.current);
        if (modalIndex !== -1) {
          globalModalStack.splice(modalIndex, 1);
        }
        
        // ✅ FIX: Replace history state instead of navigating back
        if (!isClosingFromBackButton.current && !isProcessingHistory) {
          setTimeout(() => {
            const currentState = window.history.state || {};
            if (currentState.modalCount > globalModalStack.length) {
              const updatedState = {
                ...currentState,
                modalCount: globalModalStack.length,
                timestamp: Date.now()
              };
              window.history.replaceState(updatedState, '', window.location.href);
            }
          }, 10);
        }
      }
    };
  }, []);

  return modalIdRef.current;
};

export default useModalHistory;